import { useEffect, useState, useRef } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import useExportQuotePdf from '../../hooks/useExportQuotePdf';

const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export default function PreviewQuotePdfModal({ isOpen, onClose, quote }) {
    const { getBlobUrl, download, isDownloading } = useExportQuotePdf();
    const [state, setState] = useState({ loading: false, url: null, revoke: null });
    const canvasContainerRef = useRef(null);

    useEffect(() => {
        if (!isOpen || !quote?.id) return undefined;

        let cancelled = false;
        let localRevoke = null;

        setState({ loading: true, url: null, revoke: null });
        getBlobUrl(quote.id)
            .then(async ({ url, revoke }) => {
                if (cancelled) {
                    revoke();
                    return;
                }
                localRevoke = revoke;

                if (isMobile()) {
                    // Mobile browsers can't render PDFs in iframes.
                    // Use PDF.js to render to canvases.
                    try {
                        const response = await fetch(url);
                        const arrayBuffer = await response.arrayBuffer();
                        const pdfjsLib = await import('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.mjs');
                        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.mjs';
                        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

                        if (cancelled || !canvasContainerRef.current) return;

                        const container = canvasContainerRef.current;
                        container.innerHTML = '';

                        for (let i = 1; i <= pdf.numPages; i++) {
                            const page = await pdf.getPage(i);
                            const containerWidth = container.clientWidth || 600;
                            const baseViewport = page.getViewport({ scale: 1 });
                            const scale = (containerWidth / baseViewport.width) * (window.devicePixelRatio || 2);
                            const viewport = page.getViewport({ scale });

                            const canvas = document.createElement('canvas');
                            canvas.width = viewport.width;
                            canvas.height = viewport.height;
                            canvas.style.width = '100%';
                            canvas.style.height = 'auto';
                            canvas.style.display = 'block';
                            canvas.style.marginBottom = '8px';
                            container.appendChild(canvas);

                            await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
                        }

                        setState({ loading: false, url, revoke, usePdfJs: true });
                    } catch {
                        // Fallback: just show the iframe anyway
                        setState({ loading: false, url, revoke, usePdfJs: false });
                    }
                } else {
                    setState({ loading: false, url, revoke, usePdfJs: false });
                }
            })
            .catch((err) => {
                if (cancelled) return;
                toast.error(err?.message ?? 'Failed to preview quote');
                setState({ loading: false, url: null, revoke: null });
            });

        return () => {
            cancelled = true;
            if (localRevoke) localRevoke();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, quote?.id]);

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="xl"
               centered contentClassName="h-100">
            <ModalHeader toggle={onClose}>
                Preview PDF — {quote?.quoteNumber ?? 'Quote'}
            </ModalHeader>
            <ModalBody className="p-0" style={{ height: '75vh', overflow: 'auto', background: '#525659' }}>
                {state.loading && (
                    <div className="d-flex h-100 align-items-center justify-content-center">
                        <Spinner />
                    </div>
                )}
                {!state.loading && state.url && !state.usePdfJs && (
                    <iframe
                        title="Quote PDF preview"
                        src={state.url}
                        style={{ width: '100%', height: '100%', border: 0 }}
                    />
                )}
                {/* PDF.js canvas container for mobile */}
                <div
                    ref={canvasContainerRef}
                    style={{
                        display: state.usePdfJs ? 'block' : 'none',
                        padding: '8px',
                    }}
                />
                {!state.loading && !state.url && (
                    <div className="d-flex h-100 align-items-center justify-content-center text-muted">
                        Unable to load preview.
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={onClose}>Close</Button>
                <Button color="primary"
                        onClick={() => download(quote?.id, quote?.quoteNumber)}
                        disabled={isDownloading || !quote?.id}>
                    {isDownloading && <Spinner size="sm" className="me-1" />}
                    Download
                </Button>
            </ModalFooter>
        </Modal>
    );
}
